import React, { useCallback, useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import AnchoredPopover from "./AnchoredPopover";
import gifsService from "../services/gifsService";
import { useTranslation } from "../i18n";

const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 24;

/**
 * GIF search panel: trending by default, GIPHY search as you type, more
 * results as you scroll. Calls onSelect(gif) with the chosen GIF.
 */
const GifPicker = ({ anchorRef, onSelect, onClose }) => {
  const { t } = useTranslation("common");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Guards against a slow earlier request overwriting a newer one.
  const requestIdRef = useRef(0);
  const inputRef = useRef(null);

  const load = useCallback(async (q, offset) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const page = q
        ? await gifsService.search(q, { offset, limit: PAGE_SIZE })
        : await gifsService.trending({ offset, limit: PAGE_SIZE });
      if (requestId !== requestIdRef.current) return;
      const fresh = page?.results || [];
      setResults((prev) => {
        if (offset === 0) return fresh;
        const seen = new Set(prev.map((g) => g.id));
        return prev.concat(fresh.filter((g) => !seen.has(g.id)));
      });
      setNextOffset(page?.next_offset ?? null);
    } catch {
      if (requestId === requestIdRef.current) setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const q = query.trim();
    const timer = setTimeout(() => load(q, 0), q ? SEARCH_DEBOUNCE_MS : 0);
    return () => clearTimeout(timer);
  }, [query, load]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (loading || nextOffset == null) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 200) {
      load(query.trim(), nextOffset);
    }
  };

  return (
    <AnchoredPopover
      anchorRef={anchorRef}
      onClose={onClose}
      title={t("gif.title")}
      className="bg-white border border-gray-200"
    >
      <div className="w-full sm:w-[340px] flex flex-col bg-white min-h-0 flex-1">
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("gif.search_placeholder")}
              aria-label={t("gif.search_placeholder")}
              maxLength={50}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 focus:outline-none focus:border-gray-400"
            />
          </div>
          {!query.trim() && (
            <div className="mt-1.5 text-xs font-medium text-slate-500">{t("gif.trending")}</div>
          )}
        </div>

        <div className="overflow-y-auto p-2 h-[50vh] sm:h-[360px]" onScroll={onScroll}>
          {error ? (
            <div className="text-sm text-gray-500 text-center py-8">
              <p>{t("gif.error")}</p>
              <button
                type="button"
                onClick={() => load(query.trim(), 0)}
                className="mt-2 text-sm underline text-gray-700"
              >
                {t("actions.retry")}
              </button>
            </div>
          ) : results.length === 0 && !loading ? (
            <p className="text-sm text-gray-500 text-center py-8">{t("gif.no_results")}</p>
          ) : (
            <div className="columns-2 gap-2">
              {results.map((gif) => (
                <button
                  key={gif.id}
                  type="button"
                  onClick={() => onSelect(gif)}
                  title={gif.title || undefined}
                  aria-label={gif.title || t("gif.alt")}
                  className="block w-full mb-2 rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-gray-400 break-inside-avoid"
                >
                  <img
                    src={gif.preview_url}
                    alt=""
                    loading="lazy"
                    width={gif.preview_width || undefined}
                    height={gif.preview_height || undefined}
                    className="w-full h-auto block"
                  />
                </button>
              ))}
            </div>
          )}
          {loading && (
            <p className="text-xs text-gray-500 text-center py-3">{t("gif.loading")}</p>
          )}
        </div>

        <div className="px-2 py-1.5 border-t border-gray-100 text-[11px] text-gray-400 text-right">
          {t("gif.powered_by")}
        </div>
      </div>
    </AnchoredPopover>
  );
};

/**
 * "GIF" toolbar button. Renders nothing when GIFs aren't enabled on the
 * server (no GIPHY key), so the composer never offers a dead feature.
 */
export const GifToolbarButton = ({ onSelect, className = "" }) => {
  const { t } = useTranslation("common");
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    let alive = true;
    gifsService.getStatus().then((s) => {
      if (alive) setEnabled(!!s?.enabled);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        title={t("gif.add")}
        aria-label={t("gif.add")}
        aria-expanded={open}
        className={`px-1.5 py-0.5 rounded text-[11px] font-bold tracking-wide border border-gray-400 text-gray-600 ${
          open ? "bg-gray-200" : "hover:bg-gray-100"
        } ${className}`}
      >
        GIF
      </button>
      {open && (
        <GifPicker
          anchorRef={btnRef}
          onClose={close}
          onSelect={(gif) => {
            onSelect(gif);
            close();
          }}
        />
      )}
    </>
  );
};

/** The GIF chosen in the composer, with a remove button. */
export const SelectedGif = ({ gif, onRemove }) => {
  const { t } = useTranslation("common");
  if (!gif) return null;
  return (
    <div className="px-3 pb-3 flex justify-center">
      <div className="relative overflow-hidden rounded-2xl bg-gray-100 ring-1 ring-black/5 shadow-sm">
        <img
          src={gif.preview_url || gif.url}
          alt={gif.title || t("gif.alt")}
          className="max-h-48 max-w-full w-auto block"
        />
        <button
          type="button"
          onClick={onRemove}
          aria-label={t("gif.remove")}
          title={t("gif.remove")}
          className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default GifPicker;
